import type {
  CaptureLogOptions,
  CaptureResult,
  PostHogInterface,
} from "posthog-js";
import { sanitizeConsoleLog } from "./logPrivacy";
import { withoutUrlCredentials } from "./urlCredentials";
import { mediaFailureCause } from "./mediaFailureCause";

/**
 * PostHog, asked for rather than imported.
 *
 * It is the biggest thing this application ships that draws nothing. Imported
 * here it lands in the graph of every module that reports anything — which is
 * most of the room — and so in the head's module preloads, where it is fetched
 * and parsed on the main thread before a visitor can use the page (#531).
 *
 * Every call through it returns nothing and none is ordered against a paint, so
 * the queue costs the caller only a delay it never sees: whatever is asked for
 * before the module lands is replayed, in order, once it does.
 */
/** PostHog as the module hands it out, `init` and all. */
type PostHogModule = Awaited<typeof import("posthog-js")>["default"];

let loaded: PostHogModule | null = null;
let asked = false;
/** The in-flight fetch, for a caller that must not race it. */
let landing: Promise<void> = Promise.resolve();
const queued: Array<(ph: PostHogModule) => void> = [];

function withPostHog(report: (ph: PostHogModule) => void): void {
  if (loaded) {
    report(loaded);
    return;
  }
  queued.push(report);
  if (asked) return;
  asked = true;
  landing = import("posthog-js")
    .then((module) => {
      loaded = module.default;
      for (const queuedReport of queued.splice(0)) queuedReport(module.default);
    })
    .catch(() => {
      // Telemetry that could not be fetched is telemetry that does not run —
      // the same outcome as a blocked tag, and nothing here may break the app.
      // Caught rather than left to reject because the boot watchdog reloads the
      // document on an unhandled rejection that reads like a chunk failure.
      // The queue is dropped rather than held, so a later report asks again.
      queued.length = 0;
      asked = false;
    });
}

export function onPostHog(report: (ph: PostHogInterface) => void): void {
  withPostHog(report);
}

/**
 * Settles once everything asked of PostHog so far has reached it.
 *
 * Nothing in the application waits on this — a report is not ordered against a
 * paint. A test is: it has to be able to say "and then PostHog was told", and
 * the queue above means that is a tick away rather than immediate.
 */
export function telemetrySettled(): Promise<void> {
  return landing;
}
import type {
  AnalyticsArgs,
  AnalyticsEventMap,
  AnalyticsOnceArgs,
} from "./messageContracts";
import {
  createConsoleBudget,
  createEventGuard,
  hashId,
  lengthBucket,
} from "@/engine/support/eventGuards";
import {
  deploymentEnvironment,
  type DeploymentEnvironment,
} from "@/engine/support/deploymentEnvironment";
import {
  takeRecordedChunkFailure,
  type ChunkFailureReport,
} from "@/engine/support/chunkFailure";
import {
  isDevServerStack,
  isForeignWorkerScriptException,
  isInjectedNativeBridgeException,
  isNoisyConsoleMessage,
  isNoisyErrorSource,
  shouldForwardConsole,
  shouldForwardRuntimeError,
} from "@/engine/support/logNoise";

/**
 * PostHog analytics and log/error forwarding: pageviews, runtime errors from
 * `window.onerror` and `unhandledrejection`, and console output.
 *
 * **Console output goes to Logs, not to an event.** PostHog has two separate
 * products, and an ordinary event named `console_log` lands in Events while the
 * Logs product at `/project/<id>/logs` stays empty — which is what "the logs
 * are not arriving" means. `posthog.captureLog` is the right door, and it costs
 * no event quota.
 *
 * PostHog owns console capture. The application supplies the filtering and
 * budget at the Logs boundary, so remote console enablement cannot create a
 * second owner alongside an application monkey patch.
 */

declare global {
  interface Window {
    /** The live PostHog client, exposed for debugging once `loaded` fires. */
    posthog?: PostHogInterface;
  }
}

let initialized = false;

/**
 * The analytics-consent gate. `initAnalytics` is a no-op until this is granted;
 * `applyAnalyticsConsent` is the one door the consent layer calls to flip it.
 */
let analyticsConsent = false;

interface AuthenticatedAnalyticsUser {
  id: string;
  email?: string | null;
}

/** The account waiting to be identified once analytics consent is granted. */
let authenticatedUser: AuthenticatedAnalyticsUser | null = null;
let identifiedUserId: string | null = null;

function identifyAuthenticatedUserIfPossible(): void {
  if (!initialized || !analyticsConsent || !authenticatedUser) return;
  if (identifiedUserId === authenticatedUser.id) return;
  try {
    const user = authenticatedUser;
    if (identifiedUserId) withPostHog((ph) => ph.reset());
    withPostHog((ph) =>
      ph.identify(user.id, {
        ...(user.email ? { email: user.email } : {}),
      }),
    );
    identifiedUserId = authenticatedUser.id;
  } catch {
    /* never break the app because of telemetry */
  }
}

/**
 * Identifies the authenticated account with its stable Supabase user id.
 * Email is a person property rather than an event property.
 */
export function identifyAuthenticatedUser(
  user: AuthenticatedAnalyticsUser,
): void {
  authenticatedUser = user;
  identifyAuthenticatedUserIfPossible();
}

/** Clears PostHog's persisted identity as part of a confirmed sign-out. */
export function resetAnalyticsIdentity(): void {
  authenticatedUser = null;
  if (!initialized || !identifiedUserId) return;
  try {
    withPostHog((ph) => ph.reset());
    identifiedUserId = null;
  } catch {
    /* never break the app because of telemetry */
  }
}

/**
 * Grants or withdraws analytics consent. Granting initialises PostHog (once)
 * and opts the client in; withdrawing opts a live client out for the rest of
 * the page. Never throws — telemetry must not break the app.
 */
export function applyAnalyticsConsent(granted: boolean): void {
  analyticsConsent = granted;
  if (granted) {
    initAnalytics();
    if (initialized) {
      try {
        withPostHog((ph) => ph.opt_in_capturing());
      } catch {
        /* never break the app because of telemetry */
      }
      identifyAuthenticatedUserIfPossible();
    }
    return;
  }
  if (initialized) {
    try {
      withPostHog((ph) => ph.opt_out_capturing());
    } catch {
      /* never break the app because of telemetry */
    }
  }
}

const REPLAY_BLOCK_SELECTOR = "audio, video, canvas, img, [data-ph-no-capture]";
const REPLAY_MASKED_ATTRIBUTES = new Set([
  "alt",
  "aria-label",
  "href",
  "src",
  "title",
  "value",
]);

export function maskReplayAttribute(name: string, value: string): string {
  return REPLAY_MASKED_ATTRIBUTES.has(name) || name.startsWith("data-")
    ? "***"
    : value;
}

function apiHost(): string | undefined {
  return import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
}

function serialize(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(" ")
    .slice(0, 2000);
}

/** Total per minute, and per repeated message. See `createConsoleBudget`. */
const consoleBudget = createConsoleBudget();
const mediaErrorBudget = createConsoleBudget();

const IS_DEV_BUILD = Boolean(import.meta.env.DEV);

function allowConsoleRecord(record: CaptureLogOptions): boolean {
  return (
    record.attributes?.["source"] === "room_media" ||
    consoleBudget.allow(record.body)
  );
}

export function filterConsoleLog(
  record: CaptureLogOptions,
  isDevBuild = IS_DEV_BUILD,
) {
  const consoleLevel = record.attributes?.["console_level"];
  const level =
    consoleLevel === "log" ||
    consoleLevel === "info" ||
    consoleLevel === "warn" ||
    consoleLevel === "error"
      ? consoleLevel
      : record.level === "warn" || record.level === "error"
        ? record.level
        : "log";
  if (!shouldForwardConsole(level, record.body, isDevBuild)) return null;
  const sanitized = sanitizeConsoleLog(record);
  if (!allowConsoleRecord(sanitized)) return null;
  return sanitized;
}

const guard = createEventGuard();

/**
 * Explicit product event. Safe to call before init (no-ops without a token).
 * `dedupKey` scopes the 10s dedup window (e.g. a hashed peer id).
 */
export function trackEvent(...[event, properties, dedupKey]: AnalyticsArgs) {
  if (!initialized) return;
  if (!guard.allow(event, dedupKey)) return;
  try {
    withPostHog((ph) =>
      ph.capture(event, { path: window.location.pathname, ...properties }),
    );
  } catch {
    /* never break the app because of telemetry */
  }
}

/** Fires an event at most once per `key` for the lifetime of the page. */
export function trackOnce(key: string, ...args: AnalyticsOnceArgs) {
  if (!initialized) return;
  if (!guard.allowOnce(key)) return;
  const [event, properties] = args;
  if (!guard.allow(event, key)) return;
  try {
    withPostHog((ph) =>
      ph.capture(event, { path: window.location.pathname, ...properties }),
    );
  } catch {
    /* never break the app because of telemetry */
  }
}

export { lengthBucket, hashId };

/** Handled media errors use the exception and Logs products, outside ICE noise. */
export function captureMediaFailure(
  error: unknown,
  fields: {
    operation: "session" | "publish" | "unpublish" | "receive" | "playback";
    kind: "audio" | "webcam" | "screen" | "session";
    selected_transport: "sfu" | "mesh" | "unknown";
    actual_transport: "sfu" | "mesh" | "pending" | "unknown";
    room_session_id: string | null;
  },
): void {
  if (!initialized || !analyticsConsent) return;
  const name =
    error instanceof Error || error instanceof DOMException
      ? error.name
      : "Error";
  if (name === "AbortError") return;
  // Provider bodies and SDP can contain credentials, addresses and identifiers.
  // Classify locally; only the bounded category leaves the browser.
  const message = error instanceof Error ? error.message : "";
  // Separated, not concatenated: the stale-session probe anchors `410` on a
  // word boundary, and `"410" + "Error"` leaves none for it to find.
  const { reason } = mediaFailureCause(`${message} ${name}`);
  const body = `Room media ${fields.operation} failed: ${reason}`;
  if (!mediaErrorBudget.allow(body)) return;
  const properties = {
    ...fields,
    source: "room_media",
    reason,
    app_version:
      typeof __APP_VERSION__ === "undefined" ? "unknown" : __APP_VERSION__,
  };
  try {
    withPostHog((ph) => ph.captureException(new Error(body), properties));
    withPostHog((ph) =>
      ph.captureLog({ level: "error", body, attributes: properties }),
    );
  } catch {
    /* telemetry cannot interrupt media recovery */
  }
}

/** Error captures retain their SDK path and do not spend the product budget. */
function captureOwnedError(
  ...args: {
    [
      Name in
        | "runtime-error-raised"
        | "promise-rejection-unhandled"
        | "react-error-caught"
        | "chunk-load-failed"
    ]: [event: Name, properties: AnalyticsEventMap[Name]];
  }[
    | "runtime-error-raised"
    | "promise-rejection-unhandled"
    | "react-error-caught"
    | "chunk-load-failed"]
) {
  const [event, properties] = args;
  withPostHog((ph) => ph.capture(event, properties));
}

/**
 * Reports a chunk that would not load.
 *
 * A failed module fetch is cross-origin as far as the browser is concerned, so
 * the only thing `window.onerror` carries about it is `Script error.` — a
 * report nobody can act on. This one names the chunk, the page and how many
 * attempts it took, and goes to error tracking as well as to events so it is
 * findable from either side.
 */
export function captureChunkFailure(
  report: ChunkFailureReport,
  error?: Error,
): void {
  try {
    captureOwnedError("chunk-load-failed", report);
    withPostHog((ph) =>
      ph.captureException?.(
        error ?? new Error(`Chunk failed to load: ${report.chunk}`),
        {
          source: "chunk_load",
          ...report,
        },
      ),
    );
  } catch {
    /* never break the app because of telemetry */
  }
}

/**
 * Sends the boot failure the hydration watchdog left behind, if there is one.
 *
 * Nothing could be sent while the failure was happening — the reporter is in
 * the bundle that did not arrive — so the record is read on the first load that
 * did work. That load is the recovery, which is why it is reported as one.
 */
function reportRecordedBootFailure(): void {
  try {
    const recorded = takeRecordedChunkFailure(window.sessionStorage);
    if (!recorded) return;
    captureChunkFailure({ ...recorded, recovered: true });
  } catch {
    /* never break the app because of telemetry */
  }
}

export function captureReactError(
  error: Error,
  componentStack?: string | null,
) {
  try {
    const source = "react_error_boundary";
    const boundedStack = componentStack?.slice(0, 2000);
    const path = window.location.pathname;
    captureOwnedError("react-error-caught", {
      message: error.message,
      name: error.name,
      stack: error.stack?.slice(0, 4000),
      componentStack: boundedStack,
      path,
      source,
    });
    withPostHog((ph) =>
      ph.captureException?.(error, {
        source,
        componentStack: boundedStack,
        path,
      }),
    );
  } catch {
    /* never break the app because of telemetry */
  }
}

interface ExceptionStackFrame {
  filename?: string;
  function?: string;
}
interface CapturedException {
  value?: string;
  stacktrace?: { frames?: ExceptionStackFrame[] };
}

function exceptionList(result: CaptureResult): CapturedException[] {
  const list = result.properties?.["$exception_list"];
  return Array.isArray(list) ? list : [];
}

function exceptionFrames(list: CapturedException[]): ExceptionStackFrame[] {
  return list.flatMap((exception) => exception.stacktrace?.frames ?? []);
}

/**
 * Restores the noise gate for autocaptured exceptions.
 *
 * `capture_exceptions` files a `$exception` straight to Error tracking without
 * running the `captureRuntimeErrors` filters, so a Lovable-preview or
 * dev-server crash opens an issue beside a production one. This drops any
 * `$exception` a non-production build raised, one whose whole stack sits in the
 * dev server, one thrown by a native bridge an in-app browser injects, and one
 * reporting a worker script that would not load, which no worker of this
 * application's could be. Every other event passes through untouched.
 */
export function filterExceptionEvent(
  result: CaptureResult | null,
  environment: DeploymentEnvironment,
): CaptureResult | null {
  if (!result || result.event !== "$exception") return result;
  if (environment !== "production") return null;
  const list = exceptionList(result);
  const frames = exceptionFrames(list);
  const messages = list.map((exception) => exception.value);
  if (isDevServerStack(frames.map((frame) => frame.filename))) return null;
  if (
    isInjectedNativeBridgeException(
      messages,
      frames.map((frame) => frame.function),
    )
  )
    return null;
  if (isForeignWorkerScriptException(messages)) return null;
  return result;
}

function captureRuntimeErrors() {
  window.addEventListener("error", (event) => {
    // Vite/HMR and preview-harness failures are build noise, not app errors.
    if (
      isNoisyErrorSource(event.filename) ||
      isNoisyConsoleMessage(event.message)
    )
      return;
    const stack = event.error instanceof Error ? event.error.stack : undefined;
    // Hot-reload artefacts and opaque cross-origin reports are not app errors.
    if (!shouldForwardRuntimeError(event.message, stack, IS_DEV_BUILD)) return;
    captureOwnedError("runtime-error-raised", {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      stack: stack?.slice(0, 4000),
      path: window.location.pathname,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : serialize([reason]);
    if (isNoisyConsoleMessage(message)) return;
    const rejectionStack = reason instanceof Error ? reason.stack : undefined;
    if (!shouldForwardRuntimeError(message, rejectionStack, IS_DEV_BUILD))
      return;
    captureOwnedError("promise-rejection-unhandled", {
      message,
      stack: rejectionStack?.slice(0, 4000),
      path: window.location.pathname,
    });
  });
}

export function initAnalytics() {
  if (initialized) return;
  // The analytics category gates PostHog entirely: no init, so no pageview, no
  // replay, no forwarded log until the visitor has consented.
  if (!analyticsConsent) return;
  const token = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
  const host = apiHost();
  if (!token || !host) {
    // The loud failure is for a developer running the app (`vite dev` /
    // preview), so an *un-set* variable is fixed rather than shipped unseen. A
    // variable that is present but empty is the opposite: a deliberate "run
    // without a telemetry backend", which is how the local compose stack and
    // the e2e suite are configured (`playwright.config.ts`, `scripts/e2e-local.sh`)
    // and how a Vitest run (`MODE === "test"`) already behaves. Those take the
    // silent no-op path.
    const deliberatelyDisabled = token === "" || host === "";
    if (
      IS_DEV_BUILD &&
      import.meta.env.MODE !== "test" &&
      !deliberatelyDisabled
    ) {
      const variable = token
        ? "VITE_PUBLIC_POSTHOG_HOST"
        : "VITE_PUBLIC_POSTHOG_PROJECT_TOKEN";
      throw new Error(
        `${variable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${variable} is configured`,
      );
    }
    return;
  }
  const appEnv = deploymentEnvironment(IS_DEV_BUILD, window.location.hostname);
  const appVersion =
    typeof __APP_VERSION__ === "undefined" ? "unknown" : __APP_VERSION__;

  withPostHog((ph) =>
    ph.init(token, {
      api_host: host,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      // Send uncaught browser errors through PostHog Error Tracking. Console
      // errors remain owned by the explicitly configured Logs integration below.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      // Keeps preview and dev-server exceptions out of Error tracking, then a
      // session's credentials out of every URL that is still sent, replay
      // included; see `filterExceptionEvent` and `withoutUrlCredentials`.
      before_send: (result: CaptureResult | null) =>
        withoutUrlCredentials(filterExceptionEvent(result, appEnv)),
      person_profiles: "identified_only",
      // Replay is a production diagnostic, not a local/test artifact. Masking is
      // explicit so a remote project-default change cannot expose room content.
      disable_session_recording: IS_DEV_BUILD,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
        maskAttributeFn: maskReplayAttribute,
        blockClass: "ph-no-capture",
        blockSelector: REPLAY_BLOCK_SELECTOR,
        recordCrossOriginIframes: false,
      },
      logs: {
        // Without a service name every record files itself under
        // "unknown_service", which is not a name anybody can filter by.
        serviceName: "syncani",
        serviceVersion: appVersion,
        environment: appEnv,
        captureConsoleLogs: true,
        beforeSend: filterConsoleLog,
      },
      loaded: (ph: PostHogInterface) => {
        ph.register({ app_env: appEnv, app_version: appVersion });
        window.posthog = ph;
        ph.capture("app-loaded", {
          path: window.location.pathname,
        } satisfies AnalyticsEventMap["app-loaded"]);
        reportRecordedBootFailure();
      },
    }),
  );
  // Lets every event be split between the preview build and production.
  withPostHog((ph) => {
    ph.register({ app_env: appEnv, app_version: appVersion });
    window.posthog = ph;
  });

  initialized = true;
  identifyAuthenticatedUserIfPossible();
  captureRuntimeErrors();
}

export function captureMediaRecovery(
  fields: Parameters<typeof captureMediaFailure>[1],
): void {
  if (!initialized || !analyticsConsent) return;
  const body = `Room media ${fields.operation} recovered`;
  if (!mediaErrorBudget.allow(body)) return;
  try {
    withPostHog((ph) =>
      ph.captureLog({
        level: "info",
        body,
        attributes: { ...fields, source: "room_media" },
      }),
    );
  } catch {
    /* telemetry cannot interrupt playback */
  }
}
